<template>
  <!-- Modal Overlay -->
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full max-h-screen overflow-y-auto">
      <!-- Modal Header -->
      <div class="flex items-center justify-between p-6 border-b border-gray-200">
        <h3 class="text-lg font-semibold text-gray-900">
          {{ isEdit ? 'Edit RFID Tag' : 'Add New RFID Tag' }}
        </h3>
        <button
          @click="$emit('cancel')"
          class="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Modal Content -->
      <form @submit.prevent="handleSubmit" class="p-6 space-y-6">
        <!-- Error Display -->
        <div v-if="error" class="bg-red-50 border border-red-200 rounded-md p-4">
          <div class="flex items-start">
            <svg class="h-5 w-5 text-red-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
            <div class="ml-3">
              <p class="text-sm text-red-700">{{ error }}</p>
            </div>
          </div>
        </div>

        <!-- Tag ID Field -->
        <div>
          <label for="tagId" class="block text-sm font-medium text-gray-700 mb-2">
            Tag ID
            <span class="text-red-500">*</span>
          </label>
          
          <!-- Manual Input -->
          <div class="space-y-3">
            <div class="relative">
              <input
                id="tagId"
                v-model="formData.tagId"
                type="text"
                :readonly="isEdit || isScanning"
                :class="[
                  'block w-full px-3 py-2 border rounded-md shadow-sm text-sm',
                  isScannedTag
                    ? 'border-green-500 bg-green-50 text-green-900'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500',
                  (isEdit || isScanning) ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                ]"
                placeholder="Enter tag ID manually or scan with NFC"
                @input="handleTagIdInput"
              />
              
              <!-- Scanned indicator -->
              <div v-if="isScannedTag" class="absolute inset-y-0 right-0 flex items-center pr-3">
                <span class="text-green-600 text-xs font-medium">✔️ Scanned</span>
              </div>
            </div>
            
            <!-- NFC Scan Button -->
            <div v-if="!isEdit" class="flex items-center space-x-4">
              <button
                type="button"
                @click="startNFCScan"
                :disabled="isScanning || !nfcSupported"
                :class="[
                  'flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors',
                  nfcSupported && !isScanning 
                    ? 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                ]"
              >
                <span v-if="isScanning" class="flex items-center">
                  <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Scanning...
                </span>
                <span v-else-if="nfcSupported">
                  📱 Scan NFC Tag
                </span>
                <span v-else>
                  NFC Not Available
                </span>
              </button>
              
              <button
                v-if="isScanning"
                type="button"
                @click="stopNFCScan"
                class="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
            
            <!-- NFC Info -->
            <div class="text-xs text-gray-500 space-y-1">
              <p v-if="nfcSupported" class="text-green-600">
                ✓ NFC is supported on this device
              </p>
              <p v-else class="text-amber-600">
                ⚠ {{ nfcMessage }}
              </p>
              <p v-if="isScanning" class="text-blue-600 animate-pulse">
                Hold your NFC tag close to scan...
              </p>
            </div>
          </div>
        </div>

        <!-- Name Field -->
        <div>
          <label for="name" class="block text-sm font-medium text-gray-700 mb-2">
            Tag Name
            <span class="text-red-500">*</span>
          </label>
          <input
            id="name"
            v-model="formData.name"
            type="text"
            required
            class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            placeholder="Give your tag a descriptive name"
          />
        </div>

        <!-- Device Type Field -->
        <div>
          <label for="deviceType" class="block text-sm font-medium text-gray-700 mb-2">
            Device Type
            <span class="text-red-500">*</span>
          </label>
          <select
            id="deviceType"
            v-model="formData.deviceType"
            required
            class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          >
            <option value="">Select device type</option>
            <option value="ESP32_relationship_hub">ESP32 Relationship Hub</option>
          </select>
        </div>

        <!-- Description Field -->
        <div>
          <label for="description" class="block text-sm font-medium text-gray-700 mb-2">
            Description (Optional)
          </label>
          <textarea
            id="description"
            v-model="formData.description"
            rows="3"
            class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            placeholder="Add any notes about this tag..."
          />
        </div>

        <!-- Color Field -->
        <div>
          <label for="color" class="block text-sm font-medium text-gray-700 mb-2">
            Color (Optional)
          </label>
          <div class="flex items-center space-x-3">
            <input
              id="color"
              v-model="formData.color"
              type="color"
              class="h-10 w-16 border border-gray-300 rounded cursor-pointer"
            />
            <input
              v-model="formData.color"
              type="text"
              placeholder="#FF0000"
              class="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            />
          </div>
        </div>

        <!-- E-Ink Display Image Field (ESP32 only) -->
        <div v-if="formData.deviceType === 'ESP32_relationship_hub'">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            E-Ink Display Image (Optional)
          </label>
          <div class="space-y-3">
            <!-- Image Preview -->
            <div v-if="formData.eInkImage" class="relative">
              <div class="border border-gray-300 rounded-md p-3 bg-gray-50">
                <div class="flex items-center justify-between mb-2">
                  <span class="text-sm text-gray-600">Custom display image configured</span>
                  <button
                    type="button"
                    @click="clearEInkImage"
                    class="text-red-600 hover:text-red-800 text-sm"
                  >
                    Remove
                  </button>
                </div>
                <div class="flex items-center space-x-3">
                  <canvas
                    ref="previewCanvasRef"
                    width="90"
                    height="128"
                    class="border border-gray-300 bg-white"
                    style="width: 45px; height: 64px; image-rendering: pixelated;"
                  ></canvas>
                  <div class="text-xs text-gray-500">
                    <div>90 × 128 pixels</div>
                    <div>3-color e-ink display</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Edit Image Button -->
            <button
              type="button"
              @click="showImageEditor = true"
              class="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-md text-center text-sm text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors"
            >
              <svg class="h-8 w-8 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {{ formData.eInkImage ? 'Edit Display Image' : 'Create Display Image' }}
            </button>

            <p class="text-xs text-gray-500">
              Design a custom image for the e-ink display. Supports white, black, and red colors only.
            </p>
          </div>
        </div>

        <!-- Form Actions -->
        <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            @click="$emit('cancel')"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="!isFormValid || isSubmitting"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <span v-if="isSubmitting">
              {{ isEdit ? 'Updating...' : 'Creating...' }}
            </span>
            <span v-else>
              {{ isEdit ? 'Update Tag' : 'Create Tag' }}
            </span>
          </button>
        </div>
      </form>
    </div>

    <!-- E-Ink Image Editor Modal -->
    <EInkImageEditor
      v-if="showImageEditor"
      :initialImageData="formData.eInkImage"
      @save="handleImageSave"
      @cancel="showImageEditor = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRFIDTagStore } from '../stores/rfidTagStore.js'
import nfcUtil from '../utils/nfcUtil.js'
import EInkImageEditor from './EInkImageEditor.vue'

// Props
const props = defineProps({
  tag: {
    type: Object,
    default: null
  },
  isEdit: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['save', 'cancel'])

// Store
const rfidTagStore = useRFIDTagStore()

// Local state
const formData = ref({
  tagId: '',
  name: '',
  deviceType: '',
  description: '',
  color: '#3B82F6',
  eInkImage: null
})

const error = ref('')
const isSubmitting = ref(false)
const isScanning = ref(false)
// True if the tagId was filled by NFC scan in this session
const isScannedTag = ref(false)
const nfcSupported = ref(false)
const nfcMessage = ref('')
const showImageEditor = ref(false)
const previewCanvasRef = ref(null)

// Computed
const isFormValid = computed(() => {
  return formData.value.tagId.trim() && 
         formData.value.name.trim() && 
         formData.value.deviceType
})

// Methods
const initializeForm = () => {
  if (props.isEdit && props.tag) {
    formData.value = {
      tagId: props.tag.tagId,
      name: props.tag.name,
      deviceType: props.tag.deviceType,
      description: props.tag.metadata?.description || '',
      color: props.tag.metadata?.color || '#3B82F6',
      eInkImage: props.tag.metadata?.eInkImage || null
    }
    // Only set scanned state for UI if editing and scanMethod is nfc_scan
    isScannedTag.value = props.tag.metadata?.scanMethod === 'nfc_scan'
  } else {
    // Reset form for new tag
    formData.value = {
      tagId: '',
      name: '',
      deviceType: 'ESP32_relationship_hub', // Default to the only available type
      description: '',
      color: '#3B82F6',
      eInkImage: null
    }
    isScannedTag.value = false
  }
  error.value = ''
  
  // Update preview if we have image data
  if (formData.value.eInkImage) {
    nextTick(() => {
      renderPreview()
    })
  }
}

const checkNFCSupport = async () => {
  // Always check secure context first
  if (typeof window === 'undefined' || !window.isSecureContext) {
    nfcSupported.value = false
    nfcMessage.value = 'NFC/WebNFC requires a secure (HTTPS) connection.'
    return
  }

  // Feature detection for Web NFC
  nfcSupported.value = 'NDEFReader' in window
  if (nfcSupported.value) {
    nfcMessage.value = '✓ NFC is supported on this device.'
    try {
      const permission = await nfcUtil.checkPermission()
      if (permission.granted === false) {
        nfcMessage.value = 'NFC permission required. Click scan to enable.'
      }
    } catch (error) {
      console.warn('NFC permission check failed:', error)
    }
  } else {
    nfcMessage.value = 'NFC/WebNFC is not available in this browser.'
  }
}

const startNFCScan = async () => {
  if (!nfcSupported.value || isScanning.value) {
    return
  }

  try {
    isScanning.value = true
    error.value = ''
    
    // Request permission first
    const permission = await nfcUtil.requestPermission()
    if (!permission.granted) {
      throw new Error(permission.error || 'NFC permission denied')
    }

    // Start scanning
    const tagData = await nfcUtil.startScan({
      timeout: 30000,
      onReading: (data) => {
        console.log('NFC tag detected:', data)
      },
      onError: (err) => {
        console.error('NFC scan error:', err)
      }
    })

    // Handle successful scan
    formData.value.tagId = tagData.tagId
    isScannedTag.value = true
    
    // Auto-generate name if empty
    if (!formData.value.name.trim()) {
      formData.value.name = `Tag ${tagData.tagId.substring(0, 8)}`
    }

  } catch (err) {
    console.error('NFC scan failed:', err)
    error.value = err.message || 'Failed to scan NFC tag'
  } finally {
    isScanning.value = false
  }
}

const stopNFCScan = () => {
  nfcUtil.stopScan()
  isScanning.value = false
}

const handleTagIdInput = () => {
  // If user manually types, mark as not scanned
  if (!props.isEdit) {
    isScannedTag.value = false
  }
  error.value = ''
}

const handleImageSave = (imageData) => {
  formData.value.eInkImage = imageData
  showImageEditor.value = false
  
  // Update preview
  nextTick(() => {
    renderPreview()
  })
}

const clearEInkImage = () => {
  formData.value.eInkImage = null
}

const renderPreview = () => {
  const canvas = previewCanvasRef.value
  if (!canvas || !formData.value.eInkImage) return
  
  try {
    const ctx = canvas.getContext('2d')
    ctx.imageSmoothingEnabled = false
    ctx.clearRect(0, 0, 90, 128)
    
    // Decode the image data
    const binaryString = atob(formData.value.eInkImage)
    const bytes = new Uint8Array(binaryString.length)
    
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    
    const colors = ['#FFFFFF', '#000000', '#FF0000'] // White, Black, Red
    let byteIndex = 0
    let bitOffset = 0
    
    for (let y = 0; y < 128; y++) {
      for (let x = 0; x < 90; x++) {
        // Extract 2 bits from current byte
        const pixelValue = (bytes[byteIndex] >> (6 - bitOffset)) & 0x03
        
        if (pixelValue !== 0) { // Don't draw white pixels
          ctx.fillStyle = colors[pixelValue] || '#000000'
          ctx.fillRect(x, y, 1, 1)
        }
        
        bitOffset += 2
        if (bitOffset >= 8) {
          bitOffset = 0
          byteIndex++
        }
      }
    }
  } catch (error) {
    console.error('Error rendering preview:', error)
  }
}

const handleSubmit = async () => {
  if (!isFormValid.value || isSubmitting.value) {
    return
  }

  try {
    isSubmitting.value = true
    error.value = ''

    // Check if tag ID already exists (for new tags)
    if (!props.isEdit) {
      const existingCheck = await rfidTagStore.checkTagExists(formData.value.tagId)
      if (existingCheck.exists) {
        throw new Error('A tag with this ID already exists')
      }
    }

    // Prepare tag data
    const tagData = {
      tagId: formData.value.tagId.trim(),
      name: formData.value.name.trim(),
      deviceType: formData.value.deviceType,
      metadata: {
        scanMethod: isScannedTag.value ? 'nfc_scan' : 'manual',
        description: formData.value.description.trim(),
        color: formData.value.color,
        eInkImage: formData.value.eInkImage
      }
    }

    // Emit save event
    emit('save', tagData)

  } catch (err) {
    console.error('Form submission error:', err)
    error.value = err.message || 'Failed to save tag'
  } finally {
    isSubmitting.value = false
  }
}

// Watchers
watch(() => props.tag, () => {
  initializeForm()
}, { immediate: true })

// Lifecycle
onMounted(async () => {
  initializeForm()
  await checkNFCSupport()
})

onUnmounted(() => {
  if (isScanning.value) {
    stopNFCScan()
  }
})
</script>

<style scoped>
/* Additional component styles if needed */
</style>